import { relations, sql } from 'drizzle-orm'
import {
  check,
  decimal,
  integer,
  pgEnum,
  pgTable,
  text,
} from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getDateField,
  getIdReference,
  getMonetaryField,
  getNullableIdReference,
} from '../common/utils'
import { cashCertaintyEnum, cashDirectionEnum } from './cashEnums'
import { cashEvents } from './cashEvent'
import { companies } from './company'

export const cashFlowIntervalEnum = pgEnum('cash_flow_interval', [
  'monthly',
  'quarterly',
  'yearly',
])

// A recurring cost or income the ledger cannot know yet (rent, leasing, a loan
// instalment). The rule is the thing you edit; the cash_events it generates are
// what the forecast sums. `dayRule` 31 means the last day of the month, so a
// rule lands on 28.2. and 30.4. without a second field.
export const cashFlowRules = pgTable(
  'cash_flow_rules',
  {
    ...getBaseFields(),
    companyId: getIdReference(() => companies.id),
    name: text().notNull(),
    category: text().notNull(),
    direction: cashDirectionEnum().notNull(),
    amountNet: getMonetaryField().notNull(),
    vatRate: decimal({ precision: 5, scale: 4 }).notNull(),
    certainty: cashCertaintyEnum().notNull().default('expected'),
    interval: cashFlowIntervalEnum().notNull(),
    dayRule: integer().notNull(),
    startDate: getDateField().notNull(),
    endDate: getDateField(),
    supplierMatch: text(),
    // The group company on the other side of an internal charge. Copied onto
    // every occurrence, so the group view drops it like an intercompany invoice.
    intercompanyCounterpartyCompanyId: getNullableIdReference(
      () => companies.id,
    ),
    notes: text(),
  },
  (table) => [
    ...getBaseIndexes('cash_flow_rules', table),
    check(
      'cash_flow_rules_day_rule_range',
      sql`${table.dayRule} BETWEEN 1 AND 31`,
    ),
    // A company charging itself would write a leg and a mirror that cancel out.
    check(
      'cash_flow_rules_counterparty_not_self',
      sql`${table.intercompanyCounterpartyCompanyId} <> ${table.companyId}`,
    ),
  ],
)

export const cashFlowRulesRelations = relations(
  cashFlowRules,
  ({ one, many }) => ({
    company: one(companies, {
      fields: [cashFlowRules.companyId],
      references: [companies.id],
    }),
    cashEvents: many(cashEvents),
  }),
)
